





























































































import { Component, Prop, Vue } from 'vue-property-decorator';

import axios from 'axios';
import sortBy from 'lodash/sortBy';
import { email, required, requiredIf } from 'vuelidate/lib/validators';

import { Auth0User, UserGroup, UserRole } from '@/lib/models';

interface CreateUpdateUserRequest {
  id?: string;
  email: string;
  givenName: string;
  familyName: string;
  phoneNumber: string;
  title: string;
  userRoleIds: string[];
}

@Component({
  validations: {
    form: {
      isNewUser: {
        required,
      },
      email: {
        requiredIf: requiredIf('isNewUser'),
        email,
      },
      firstName: {
        required,
      },
      lastName: {
        required,
      },
    },
  },
})
export default class CreateEditUserModal extends Vue {
  @Prop({ required: true }) private readonly user!: Auth0User;
  @Prop({ type: Array, required: true }) private readonly userRoles!: UserRole[];
  @Prop({ type: Array, required: true }) private readonly userGroups!: UserGroup[];

  private form: any = {
    isNewUser: this.user === null,
    email: null,
    firstName: null,
    lastName: null,
    phone: null,
    title: null,
  };

  private selectedUserRoleIds: string[] = [];
  private userRoleIdToAdd: string = '';
  private userRoleIdToRemove: string = '';

  private submitting: boolean = false;

  private onShow(): void {
    // Using $nextTick ensures that changes to the `user` prop in the parent
    // component will have had time to propagate down to this component
    this.$nextTick(() => {
      if (this.user === null) {
        this.resetForm();
      } else {
        this.prepopulateForm();
      }
    });
  }

  private resetForm(): void {
    this.form.isNewUser = true;
    this.form.email = null;
    this.form.firstName = null;
    this.form.lastName = null;
    this.form.phone = null;
    this.form.title = null;
    this.selectedUserRoleIds = [];
    this.userRoleIdToAdd = '';
    this.userRoleIdToRemove = '';
    this.$v.form.$reset();
  }

  private prepopulateForm(): void {
    this.form.isNewUser = false;
    this.form.email = this.user.email;
    this.form.firstName = this.user.given_name;
    this.form.lastName = this.user.family_name;
    this.form.phone = this.user.app_metadata.phone_number;
    this.form.title = this.user.app_metadata.title;
    this.selectedUserRoleIds = this.user.app_metadata.user_role_ids;
    this.userRoleIdToAdd = '';
    this.userRoleIdToRemove = '';
    this.$v.form.$touch();
  }

  private get masterUserGroupId(): string {
    return this.userGroups.find((userGroup) => userGroup.isMaster)!.id!;
  }

  private get addableUserRoleOptions(): any[] {
    return this.userRoles
      .filter((userRole) => !this.selectedUserRoleIds.includes(userRole.id))
      .map((userRole) => {
        return {
          value: userRole.id,
          text: userRole.name,
        };
      });
  }

  private get sortedAddableUserRoleOptions(): any[] {
    return sortBy(this.addableUserRoleOptions, 'text');
  }

  private addSelectedUserRole(): void {
    const userRoleIdToAdd = this.userRoleIdToAdd;
    this.userRoleIdToAdd = '';
    this.selectedUserRoleIds.push(userRoleIdToAdd);
  }

  private get removableUserRoleOptions(): any[] {
    return this.userRoles
      .filter((userRole) => this.selectedUserRoleIds.includes(userRole.id))
      .map((userRole) => {
        return {
          value: userRole.id,
          text: userRole.name,
        };
      });
  }

  private get sortedRemovableUserRoleOptions(): any[] {
    return sortBy(this.removableUserRoleOptions, 'text');
  }

  private removeSelectedUserRole(): void {
    this.selectedUserRoleIds = this.selectedUserRoleIds
      .filter((userRoleId: string) => this.userRoleIdToRemove !== userRoleId);
  }

  private handleOk(bvModalEvent: Event): void {
    bvModalEvent.preventDefault();
    this.submitting = true;
    const userData: any = {
      givenName: this.form.firstName,
      familyName: this.form.lastName,
      title: this.form.title,
      phoneNumber: this.form.phone,
      email: this.form.email,
      isActive: this.user?.app_metadata['is_active'],
      userRoleIds: this.selectedUserRoleIds,
      // Don't change user groups when updating an existing user
      userGroupIds: this.form.isNewUser ? [this.masterUserGroupId] : undefined,
    };
    if (this.form.isNewUser) {
      this.createUser(userData);
    } else {
      this.updateUser(userData);
    }
  }

  private async createUser(user: any): Promise<void> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.post(url, user, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully created user: ${user.email}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'User Created',
        okVariant: 'success',
        centered: true,
      }).then(() => {
        this.$emit('create-user');
        this.$nextTick(() => {
          (this.$refs.modal as any).hide();
        });
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Creating User',
        okVariant: 'danger',
        centered: true,
      });
    }).finally(() => {
      this.submitting = false;
    });
  }

  private async updateUser(user: any): Promise<void> {
    // Use encodeURIComponent() to escape the vertical bar ('|') character in the Auth0 user ID
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/new_users/${encodeURIComponent(this.user.user_id)}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.put(url, user, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully updated user: ${user.email}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'User Updated',
        okVariant: 'success',
        centered: true,
      }).then(() => {
        this.$emit('update-user');
        this.$nextTick(() => {
          (this.$refs.modal as any).hide();
        });
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Updating User',
        okVariant: 'danger',
        centered: true,
      });
    }).finally(() => {
      this.submitting = false;
    });
  }
}
